import { Button } from "@mui/material";
import { useWebStoreContext } from "contexts";
import { useEffect, useState } from "react";
import {
	useClearDebugExperimentsMutation,
	useGetDebugExperimentsQuery,
	useSetDebugExperimentsMutation
} from "redux/apis";
import { Container, ErrorMessage, LoadingMessage } from "./styles";

interface ExperimentsOverrideProps {
	onSave: () => void;
}

const ExperimentsOverride = ({ onSave }: ExperimentsOverrideProps) => {
	const { authToken } = useWebStoreContext();

	const [values, setValues] = useState("");
	const [errorMessage, setErrorMessage] = useState<
		string | number | undefined
	>();
	const {
		data,
		isLoading: queryIsLoading,
		error: queryError
	} = useGetDebugExperimentsQuery("", {
		refetchOnFocus: true,
		skip: !authToken?.value
	});
	const [
		createExperiments,
		{ isLoading: createIsLoading, error: createError }
	] = useSetDebugExperimentsMutation();

	const [clearExperiments, { isLoading: deleteIsLoading, error: deleteError }] =
		useClearDebugExperimentsMutation();

	const handleSave = async () => {
		setErrorMessage("");
		// prevent CRLF injection override
		const formatedValues = values.replace(/\r?\n|\r/g, "");
		const data = await createExperiments(formatedValues).unwrap();
		if (data?.userTracker?.experiments) {
			syncValues(data.userTracker.experiments);
		}
		onSave();
	};

	const handleDelete = () => {
		setErrorMessage("");
		clearExperiments("")
			.unwrap()
			.then(() => {
				setValues("");
			});
	};

	const syncValues = (data: Array<string>) => {
		const formattedData = `[\n    ${data
			.map(
				(item: string, i: number) =>
					`"${item}"${data.length - 1 !== i ? "," : ""}`
			)
			.join("\n    ")}\n]`;
		setValues(formattedData);
	};

	useEffect(() => {
		if (data?.experiments) {
			syncValues(data.experiments);
		}
	}, [data]);

	useEffect(() => {
		if (createError) {
			setErrorMessage(
				"status" in createError
					? "error" in createError
						? createError.error
						: createError.status
					: createError.message
			);
		}
	}, [createError]);

	useEffect(() => {
		if (deleteError) {
			setErrorMessage(
				"status" in deleteError
					? "error" in deleteError
						? deleteError.error
						: deleteError.status
					: deleteError.message
			);
		}
	}, [deleteError]);

	if (queryIsLoading) {
		return <LoadingMessage>Loading Experiments Override...</LoadingMessage>;
	}

	if (queryError) {
		return (
			<ErrorMessage>
				Error:
				{"status" in queryError
					? "error" in queryError
						? queryError.error
						: queryError.status
					: queryError.message}
			</ErrorMessage>
		);
	}

	return (
		<Container>
			<h2>Experiments Override</h2>
			<textarea
				id="txtArea"
				name="txtArea"
				rows={43}
				style={{ width: "700px" }}
				value={values}
				onChange={(e) => setValues(e.target.value)}
			/>
			{errorMessage && <ErrorMessage>Error: {errorMessage}</ErrorMessage>}
			<div>
				<Button
					variant="outlined"
					sx={{ margin: "10px" }}
					onClick={handleDelete}
					disabled={deleteIsLoading}
				>
					Delete
				</Button>
				<Button
					variant="outlined"
					sx={{ margin: "10px" }}
					onClick={handleSave}
					disabled={createIsLoading}
				>
					Set Override
				</Button>
			</div>
		</Container>
	);
};

export default ExperimentsOverride;
